import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
// Removed subscription manager import - using enhanced version only in TournamentContext
import { handleAsyncOperation } from './shared/errorHandler';
import { getAllTeamsWithStats, getTeamStats, promoteToTeamCaptain, demoteFromTeamCaptain, transferTeamOwnership, sendTeamInvitation, updateTeamInfo, getTeamInvitations, deleteTeam as deleteTeamService } from '../services/database';

const TeamContext = createContext();

// Team-specific actions
const teamActions = {
  SET_TEAMS: 'SET_TEAMS',
  SET_USER_TEAMS: 'SET_USER_TEAMS',
  SET_USER_INVITES: 'SET_USER_INVITES',
  ADD_TEAM: 'ADD_TEAM',
  UPDATE_TEAM: 'UPDATE_TEAM',
  REMOVE_INVITE: 'REMOVE_INVITE'
};

// Team-specific reducer
const teamReducer = (state, action) => {
  switch (action.type) {
    case teamActions.SET_TEAMS:
      return { ...state, teams: action.payload, loading: false };
    case teamActions.SET_USER_TEAMS:
      return { ...state, userTeams: action.payload };
    case teamActions.SET_USER_INVITES:
      return { ...state, userInvites: action.payload };
    case teamActions.ADD_TEAM:
      return {
        ...state,
        teams: [...state.teams, action.payload],
        userTeams: [...state.userTeams, action.payload]
      };
    case teamActions.UPDATE_TEAM:
      const updatedTeams = state.teams.map(team =>
        team.id === action.payload.id ? action.payload : team
      );
      const updatedUserTeams = state.userTeams.map(team =>
        team.id === action.payload.id ? action.payload : team
      );
      return {
        ...state,
        teams: updatedTeams,
        userTeams: updatedUserTeams
      };
    case teamActions.REMOVE_INVITE:
      return {
        ...state,
        userInvites: state.userInvites.filter(invite => invite.id !== action.payload)
      };
    default:
      return state;
  }
};

const initialState = createBaseState({
  teams: [],
  userTeams: [],
  userInvites: []
});

export const TeamProvider = ({ children }) => {
  const { state, dispatch, setLoading, clearError } = useBaseContext(initialState, teamReducer);
  const { user } = useAuth();

  // Shared helper function to fetch and transform all teams data
  const _fetchAllTeamsData = useCallback(async () => {
    const { data: allTeams, error: allTeamsError } = await supabase
      .from('teams')
      .select(`
        *,
        owner:users!teams_owner_id_fkey(username, display_name, avatar_url),
        members:team_members(
          user:users(id, username, display_name, avatar_url),
          role,
          joined_at
        )
      `)
      .eq('is_active', true)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (allTeamsError) throw allTeamsError;

    return (allTeams || []).map(team => ({
      id: team.id,
      name: team.name,
      description: team.description || '',
      logo: team.logo || '🏆',
      country_code: team.country_code,
      recruitment_status: team.recruitment_status,
      owner: team.owner?.username || team.owner?.display_name || 'Owner',
      ownerId: team.owner_id,
      ownerName: team.owner?.username || team.owner?.display_name,
      ownerAvatarUrl: team.owner?.avatar_url,
      members: team.members || [],
      stats: {
        wins: team.wins || 0,
        losses: team.losses || 0,
        draws: 0,
        tournamentsWon: team.tournaments_won || 0,
        rank: team.elo_rating || 1000
      },
      createdAt: team.created_at,
      isActive: team.is_active
    }));
  }, []);

  const loadAllTeamsData = useCallback(async () => {
    return handleAsyncOperation(async () => {
      const result = await getAllTeamsWithStats();
      if (result.success) {
        dispatch({ type: teamActions.SET_TEAMS, payload: result.data });
      } else {
        throw new Error(result.error || 'Failed to load teams');
      }
    });
  }, [dispatch]);

  const loadTeams = useCallback(async () => {
    return handleAsyncOperation(async () => {
      if (!user) return;
      
      // Get teams where user is a member
      const { data: membershipData, error: membershipError } = await supabase
        .from('team_members')
        .select(`
          role,
          joined_at,
          team:teams(
            *,
            owner:users!teams_owner_id_fkey(username, display_name, avatar_url),
            members:team_members(
              user:users(id, username, display_name, avatar_url),
              role,
              joined_at
            )
          )
        `)
        .eq('user_id', user.id)
        .order('joined_at', { ascending: false });

      if (membershipError) throw membershipError;

      const transformedTeams = await Promise.all((membershipData || [])
        .filter(membership => membership.team && membership.team.is_active && !membership.team.deleted_at)
        .map(async (membership) => {
        const team = membership.team;
        let dynamicStats = {
          wins: 0,
          losses: 0,
          draws: 0,
          tournamentsWon: 0,
          rank: 1000
        };

        try {
          const teamStats = await getTeamStats(team.id);
          if (teamStats) {
            dynamicStats = {
              wins: teamStats.match_wins || 0,
              losses: teamStats.match_losses || 0,
              draws: 0,
              tournamentsWon: teamStats.tournaments_won || 0,
              totalGoals: teamStats.total_team_goals || 0,
              totalMatches: teamStats.total_matches || 0,
              rank: 1000
            };
          }
        } catch (error) {
          console.warn('Failed to get dynamic stats for team:', team.id, error);
        }

        return {
          id: team.id,
          name: team.name,
          description: team.description || '',
          logo: team.logo || '🏆',
          country_code: team.country_code,
          recruitment_status: team.recruitment_status,
          owner: team.owner?.username || team.owner?.display_name || 'Owner',
          ownerId: team.owner_id,
          ownerName: team.owner?.username || team.owner?.display_name,
          ownerAvatarUrl: team.owner?.avatar_url,
          members: team.members || [],
          role: membership.role,
          stats: dynamicStats,
          createdAt: team.created_at,
          joinedAt: membership.joined_at,
          isActive: team.is_active
        };
      }));

      dispatch({ type: teamActions.SET_USER_TEAMS, payload: transformedTeams });
    });
  }, [user, dispatch]);

  const loadUserInvites = useCallback(async () => {
    return handleAsyncOperation(async () => {
      if (!user) return;

      const { data: invites, error } = await supabase
        .from('team_invitations')
        .select(`
          *,
          team:teams(id, name, logo),
          invitedByUser:users!team_invitations_invited_by_fkey(username, display_name)
        `)
        .eq('invited_user_id', user.id)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedInvites = (invites || []).map(invite => ({
        id: invite.id,
        team: invite.team,
        invitedBy: invite.invitedByUser?.username || invite.invitedByUser?.display_name || 'Someone',
        message: invite.message,
        createdAt: invite.created_at,
        expiresAt: invite.expires_at
      }));

      dispatch({ type: teamActions.SET_USER_INVITES, payload: transformedInvites });
    });
  }, [user, dispatch]);

  useEffect(() => {
    // Always load all teams data (public)
    loadAllTeamsData();
    
    if (user) {
      // Load user-specific data only when logged in
      loadTeams();
      loadUserInvites();
    } else {
      // Clear user-specific data when logged out
      dispatch({ type: teamActions.SET_USER_TEAMS, payload: [] });
      dispatch({ type: teamActions.SET_USER_INVITES, payload: [] });
    }
  }, [user, dispatch, loadAllTeamsData, loadTeams, loadUserInvites]);

  const createTeam = useCallback(async (teamData) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in to create a team');
      
      setLoading(true);
      clearError();

      const newTeamData = {
        name: teamData.name,
        description: teamData.description || '',
        country_code: teamData.country_code,
        recruitment_status: teamData.recruitment_status || 'closed',
        logo: teamData.logo || '🏆',
        owner_id: user.id,
        is_active: true
      };

      const { data: team, error: teamError } = await supabase
        .from('teams')
        .insert(newTeamData)
        .select()
        .single();

      if (teamError) throw teamError;

      const { error: memberError } = await supabase
        .from('team_members')
        .insert({
          team_id: team.id,
          user_id: user.id,
          role: 'owner'
        });

      if (memberError) {
        console.error('Failed to create team member relationship:', memberError);
        // Clean up the created team if member relationship fails
        try {
          await supabase.from('teams').delete().eq('id', team.id);
        } catch (cleanupError) {
          console.error('Failed to cleanup team:', cleanupError);
        }
        throw new Error('Failed to create team member relationship');
      }

      const newTeam = {
        id: team.id,
        name: team.name,
        description: team.description || '',
        logo: team.logo || '🏆',
        owner: user.username || 'Owner',
        ownerId: user.id,
        ownerName: user.username || user.displayName,
        ownerAvatarUrl: user.avatarUrl,
        members: [{
          user: {
            id: user.id,
            username: user.username,
            display_name: user.displayName,
            avatar_url: user.avatarUrl
          },
          role: 'owner',
          joined_at: new Date().toISOString()
        }],
        role: 'owner',
        stats: {
          wins: 0,
          losses: 0,
          draws: 0,
          tournamentsWon: 0,
          rank: 1000
        },
        createdAt: team.created_at,
        joinedAt: new Date().toISOString(),
        isActive: true
      };

      dispatch({ type: teamActions.ADD_TEAM, payload: newTeam });
      
      // Refresh all teams data to include the new team
      await loadAllTeamsData();
      
      setLoading(false);
      
      return { success: true, data: newTeam };
    });
  }, [user, setLoading, clearError, dispatch, loadAllTeamsData]);

  const updateTeam = useCallback(async (teamId, updates) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);
      clearError();

      const result = await updateTeamInfo(teamId, updates, user.id);
      
      if (result.success) {
        const currentTeam = state.userTeams.find(t => t.id === teamId);
        if (currentTeam) {
          const updatedTeam = { ...currentTeam, ...updates };
          dispatch({ type: teamActions.UPDATE_TEAM, payload: updatedTeam });
        }

        setLoading(false);
        return { success: true, team: result.data };
      } else {
        throw new Error(result.error || 'Failed to update team');
      }
    });
  }, [user, setLoading, clearError, state.userTeams, dispatch]);

  const inviteToTeam = useCallback(async (teamId, userToInvite, message = '') => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);
      clearError();

      // userToInvite is now an object with { id, username, displayName, title }
      const targetUserId = userToInvite.id;

      if (!targetUserId) {
        throw new Error('Invalid user selected');
      }

      const result = await sendTeamInvitation(teamId, targetUserId, message, user.id);
      
      if (result.success) {
        setLoading(false);
        return { success: true, data: result.data };
      } else {
        throw new Error(result.error || 'Failed to send team invitation');
      }
    });
  }, [user, setLoading, clearError]);

  const respondToInvitation = useCallback(async (inviteId, response) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);

      const { data: invite, error: updateError } = await supabase
        .from('team_invitations')
        .update({
          status: response,
          responded_at: new Date().toISOString()
        })
        .eq('id', inviteId)
        .eq('invited_user_id', user.id)
        .select('team_id')
        .single();

      if (updateError) throw updateError;

      if (response === 'accepted') {
        const { error: memberError } = await supabase
          .from('team_members')
          .insert({
            team_id: invite.team_id,
            user_id: user.id,
            role: 'player'
          });

        if (memberError) throw memberError;
        await loadTeams();
      }

      dispatch({ type: teamActions.REMOVE_INVITE, payload: inviteId });
      setLoading(false);
      return { success: true };
    });
  }, [user, setLoading, dispatch, loadTeams]);

  const removeMember = useCallback(async (teamId, memberId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);

      const { data, error } = await supabase
        .rpc('remove_team_member', {
          team_id_param: teamId,
          user_id_param: memberId,
          requesting_user_id: user.id
        });

      if (error) throw error;
      
      if (data && data.success === false) {
        throw new Error(data.error || 'Failed to remove team member');
      }

      await loadTeams();
      setLoading(false);
      return { success: true };
    });
  }, [user, setLoading, loadTeams]);

  const leaveTeam = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);

      // Check if user is the owner - owners cannot leave, they must delete the team
      const team = state.userTeams.find(t => t.id === teamId);
      if (team && team.role === 'owner') {
        throw new Error('Team owners cannot leave the team. You must delete the team or transfer ownership first.');
      }

      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('team_id', teamId)
        .eq('user_id', user.id);

      if (error) throw error;

      await loadTeams();
      setLoading(false);
      return { success: true };
    });
  }, [user, setLoading, state.userTeams, loadTeams]);

  const getPendingInvites = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      const result = await getTeamInvitations(teamId, user.id);
      
      if (result.success) {
        const transformedInvites = (result.data || []).map(invite => ({
          id: invite.invitation_id,
          status: invite.status,
          invitedUser: {
            id: invite.invited_user_id,
            username: invite.username || 'Unknown',
            display_name: invite.display_name,
            avatar_url: invite.avatar_url
          },
          message: invite.message,
          createdAt: invite.created_at,
          expiresAt: invite.expires_at
        }));

        return { success: true, data: transformedInvites };
      } else {
        throw new Error(result.error || 'Failed to fetch team invitations');
      }
    });
  }, [user]);

  const cancelInvitation = useCallback(async (invitationId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);

      

      
      const { data: deleteData, error: deleteError } = await supabase
        .from('team_invitations')
        .delete()
        .eq('id', invitationId)
        .select('id');
      
      if (deleteError) {
        console.error('Failed to cancel invitation:', deleteError);
        throw new Error(`Failed to cancel invitation: ${deleteError.message}`);
      }
      
      if (!deleteData || deleteData.length === 0) {
        throw new Error('Failed to cancel invitation. You may not have permission to cancel this invitation.');
      }
      setLoading(false);
      return { success: true };
    });
  }, [user, setLoading]);

  const deleteTeam = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);
      clearError();

      const result = await deleteTeamService(teamId, user.id);
      
      if (result.success) {
        // Refresh team data to reflect the deletion
        await loadTeams();
        await loadAllTeamsData();
        setLoading(false);
        return { success: true, message: result.message };
      } else {
        throw new Error(result.error || 'Failed to delete team');
      }
    });
  }, [user, setLoading, clearError, loadTeams, loadAllTeamsData]);

  const getTeamById = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      const { data: team, error } = await supabase
        .from('teams')
        .select(`
          *,
          owner:users!teams_owner_id_fkey(id, username, display_name, avatar_url),
          members:team_members(
            user:users(id, username, display_name, avatar_url),
            role,
            joined_at
          )
        `)
        .eq('id', teamId)
        .eq('is_active', true)
        .is('deleted_at', null)
        .single();

      if (error) throw error;
      if (!team) throw new Error('Team not found');

      // Get dynamic stats
      let dynamicStats = {
        wins: 0,
        losses: 0,
        draws: 0,
        tournamentsWon: 0,
        rank: 1000
      };

      try {
        const teamStats = await getTeamStats(team.id);
        if (teamStats) {
          dynamicStats = {
            wins: teamStats.match_wins || 0,
            losses: teamStats.match_losses || 0,
            draws: 0,
            tournamentsWon: teamStats.tournaments_won || 0,
            totalGoals: teamStats.total_team_goals || 0,
            totalMatches: teamStats.total_matches || 0,
            rank: 1000
          };
        }
      } catch (error) {
        console.warn('Failed to get dynamic stats for team:', team.id, error);
      }

      const transformedTeam = {
        id: team.id,
        name: team.name,
        description: team.description || '',
        logo: team.logo || '🏆',
        recruitment_status: team.recruitment_status,
        owner: team.owner,
        ownerId: team.owner_id,
        ownerName: team.owner?.username || team.owner?.display_name,
        ownerAvatarUrl: team.owner?.avatar_url,
        members: team.members || [],
        stats: dynamicStats,
        createdAt: team.created_at,
        isActive: team.is_active
      };

      return { success: true, data: transformedTeam };
    });
  }, []);
  const getUserTeamsList = useCallback(() => state.userTeams, [state.userTeams]);
  const getUserInvites = useCallback(() => state.userInvites, [state.userInvites]);
  const refreshTeams = useCallback(() => {
    // Always refresh all teams data
    loadAllTeamsData();
    
    if (user) {
      // Also refresh user-specific data when logged in
      loadTeams();
      loadUserInvites();
    }
  }, [user, loadAllTeamsData, loadTeams, loadUserInvites]);

  const getAllTeams = useCallback(async () => {
    return handleAsyncOperation(async () => {
      const transformedAllTeams = await _fetchAllTeamsData();
      return { success: true, data: transformedAllTeams };
    });
  }, [_fetchAllTeamsData]);

  const updateTeamInState = useCallback((teamId, updates) => {
    // Find the current team and merge updates
    const currentTeam = state.teams.find(t => t.id === teamId) || 
                       state.userTeams.find(t => t.id === teamId);
    
    if (currentTeam) {
      const updatedTeam = { ...currentTeam, ...updates };
      dispatch({ 
        type: teamActions.UPDATE_TEAM, 
        payload: updatedTeam
      });
    } else {
      console.warn('⚠️ Team not found in state:', teamId);
    }
  }, [state.teams, state.userTeams, dispatch]);

  // Join request functions
  const submitJoinRequest = useCallback(async (teamId, message = null) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in to request to join teams');
      
      const { data, error } = await supabase.rpc('submit_join_request', {
        team_id_param: teamId,
        message_param: message
      });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const result = data[0];
        if (!result.success) {
          throw new Error(result.error);
        }
        return { success: true, requestId: result.request_id };
      }
      
      return { success: true };
    });
  }, [user]);

  const getTeamJoinRequests = useCallback(async (teamId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      const { data, error } = await supabase.rpc('get_team_join_requests', {
        team_id_param: teamId
      });
      
      if (error) throw error;
      
      return { success: true, data: data || [] };
    });
  }, [user]);

  const respondToJoinRequest = useCallback(async (requestId, response, role = 'player') => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      const { data, error } = await supabase.rpc('respond_to_join_request', {
        request_id_param: requestId,
        response_param: response,
        add_as_role: role
      });
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const result = data[0];
        if (!result.success) {
          throw new Error(result.error);
        }
      }
      
      // Refresh teams if approved to show new member
      if (response === 'approved') {
        await loadTeams();
      }
      
      return { success: true };
    });
  }, [user, loadTeams]);

  // ========================================
  // 👑 TEAM ROLE MANAGEMENT
  // ========================================

  const promoteToCaptain = useCallback(async (teamId, userId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);
      clearError();

      const result = await promoteToTeamCaptain(teamId, userId, user.id);
      
      if (result.success) {
        await loadTeams();
        setLoading(false);
        return { success: true, data: result.data };
      } else {
        throw new Error(result.error || 'Failed to promote member to captain');
      }
    });
  }, [user, setLoading, clearError, loadTeams]);

  const demoteFromCaptain = useCallback(async (teamId, userId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);
      clearError();

      const result = await demoteFromTeamCaptain(teamId, userId, user.id);
      
      if (result.success) {
        await loadTeams();
        setLoading(false);
        return { success: true, data: result.data };
      } else {
        throw new Error(result.error || 'Failed to demote captain to player');
      }
    });
  }, [user, setLoading, clearError, loadTeams]);

  const transferOwnership = useCallback(async (teamId, newOwnerId) => {
    return handleAsyncOperation(async () => {
      if (!user) throw new Error('You must be logged in');
      
      setLoading(true);
      clearError();

      const result = await transferTeamOwnership(teamId, newOwnerId, user.id);
      
      if (result.success) {
        await loadTeams();
        setLoading(false);
        return { success: true, data: result.data };
      } else {
        throw new Error(result.error || 'Failed to transfer team ownership');
      }
    });
  }, [user, setLoading, clearError, loadTeams]);

  // Permission helper functions
  const canManageTeam = useCallback((userRole) => {
    return ['owner', 'captain'].includes(userRole);
  }, []);

  const canPromoteMembers = useCallback((userRole) => {
    return userRole === 'owner';
  }, []);

  const canRemoveMember = useCallback((userRole, targetRole) => {
    if (userRole === 'owner') return targetRole !== 'owner';
    if (userRole === 'captain') return targetRole === 'player';
    return false;
  }, []);

  const getUserRole = useCallback((teamId, userId) => {
    const team = state.userTeams.find(t => t.id === teamId);
    if (!team) return null;
    
    if (team.ownerId === userId) return 'owner';
    
    const member = team.members?.find(m => m.user.id === userId);
    return member?.role || null;
  }, [state.userTeams]);

  const value = useMemo(() => ({
    ...state,
    createTeam,
    updateTeam,
    updateTeamInState,
    inviteToTeam,
    respondToInvitation,
    removeMember,
    leaveTeam,
    getPendingInvites,
    cancelInvitation,
    deleteTeam,
    getTeamById,
    getUserTeams: getUserTeamsList,
    getUserInvites,
    clearError,
    refreshTeams,
    getAllTeams,
    submitJoinRequest,
    getTeamJoinRequests,
    respondToJoinRequest,
    promoteToCaptain,
    demoteFromCaptain,
    transferOwnership,
    canManageTeam,
    canPromoteMembers,
    canRemoveMember,
    getUserRole
  }), [
    state,
    createTeam,
    updateTeam,
    updateTeamInState,
    inviteToTeam,
    respondToInvitation,
    removeMember,
    leaveTeam,
    getPendingInvites,
    cancelInvitation,
    deleteTeam,
    getTeamById,
    getUserTeamsList,
    getUserInvites,
    clearError,
    refreshTeams,
    getAllTeams,
    submitJoinRequest,
    getTeamJoinRequests,
    respondToJoinRequest,
    promoteToCaptain,
    demoteFromCaptain,
    transferOwnership,
    canManageTeam,
    canPromoteMembers,
    canRemoveMember,
    getUserRole
  ]);

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};

export const useTeams = () => {
  const context = useContext(TeamContext);
  if (!context) throw new Error('useTeams must be used within a TeamProvider');
  return context;
};

export default TeamContext;
